<template>
  <NuxtErrorBoundary @error="handleError">
    <slot />
  </NuxtErrorBoundary>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue'
import * as Sentry from '@sentry/nuxt'
import { attachBoundaryMeta } from '~/utils/errorBoundaryMeta'
import { getRouteNameWithoutLanguage } from '~/utils/getRouteNameWithoutLanguage'

const { name = '' } = defineProps<{
  /** Overrides the auto-detected host component name used for Sentry grouping. */
  name?: string
}>()

const CHUNK_LOAD_ERROR_MESSAGES = [
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
  'Failed to fetch',
]

// @sentry/nuxt captures `vue:error` from inside a deferred macrotask, which is
// scheduled after this handler runs. Reloading synchronously — or flushing on
// the next tick — would drain an empty queue and navigate away before the event
// is ever built, which is why chunk load errors never reached Sentry.
const CHUNK_RELOAD_DELAY_MS = 50
const SENTRY_FLUSH_TIMEOUT_MS = 2000

const config = useRuntimeConfig()
const route = useRoute()
const instance = getCurrentInstance()

// The component whose template renders this boundary, e.g. `EventBuyBoxHeader`.
// SFCs carry `__name`; `name` covers components declared via defineComponent.
const parentType = instance?.parent?.type as
  | { __name?: string; name?: string }
  | undefined

const boundaryName = name || parentType?.__name || parentType?.name || 'unknown'

const handleError = (e: Error) => {
  const isChunkLoadError = CHUNK_LOAD_ERROR_MESSAGES.some((message) =>
    e?.message?.includes(message)
  )

  // Runs before NuxtErrorBoundary forwards the error to the `vue:error` hook,
  // so the metadata is in place by the time Sentry builds the event. Tagging
  // rather than capturing here keeps it to a single, enriched Sentry issue.
  attachBoundaryMeta(e, {
    boundary: boundaryName,
    routeName: getRouteNameWithoutLanguage(route.name as string),
    isChunkLoadError,
  })

  if (config.public.appEnv === 'development') {
    console.error(`[ErrorBoundary: ${boundaryName}]`, e)
  }

  if (isChunkLoadError) {
    setTimeout(() => {
      Sentry.flush(SENTRY_FLUSH_TIMEOUT_MS).finally(() => {
        window?.location?.reload()
      })
    }, CHUNK_RELOAD_DELAY_MS)
  }
}
</script>
